// AgentBro conversation list — server-authoritative via Hermes' session API
// (GET/DELETE/PATCH /sessions), with a thin localStorage CACHE so the global
// sidebar renders instantly without waking the sandbox on every page.
//
// Why a cache: the sidebar is app-wide; calling the broker (which wakes the
// Daytona sandbox) from it on every page would be wasteful. So pages that
// already hold a live session (AgentBro full-page + the /chart sidebar) call
// refreshChats() → fetch GET /sessions → write the cache → sidebar updates.
// Reads (listChats) are cache-only (instant). Mutations (delete/rename) hit the
// server when creds are available.
//
// Session id == our conversation id: the chat POST sends `X-Hermes-Session-Id:
// <convId>`, so Hermes stores the thread under that exact id and
// GET /sessions/{convId}/messages replays it (see sseClient.js + HermesChat).

// Canonical per-user key. Callers pass a userId, but the sources diverge — the
// global Sidebar passes useAuth().user?.id (can be undefined under a token-only
// login) while the agent pages pass localStorage 'userId'. Keying off the single
// canonical localStorage 'userId' keeps every surface (sidebar, page, /chart tab)
// on the SAME cache, regardless of the param. Falls back to the param then 'anon'.
function canonicalUid(passed) {
  try {
    const ls = localStorage.getItem('userId');
    if (ls) return ls;
  } catch { /* no localStorage */ }
  return passed != null ? String(passed) : 'anon';
}
const LIST_KEY = (userId) => `agentbro_sessions_${canonicalUid(userId)}`;
const CREDS_KEY = 'agentbro_creds';
const MAX = 50;

// ── creds cache (written by useHermesSession) so sidebar actions can call the worker ──
export function cacheCreds(baseUrl, token, exp) {
  try { localStorage.setItem(CREDS_KEY, JSON.stringify({ baseUrl, token, exp: exp || 0 })); } catch { /* quota */ }
}
export function getCreds() {
  try {
    const c = JSON.parse(localStorage.getItem(CREDS_KEY) || 'null');
    if (!c || !c.baseUrl || !c.token) return null;
    if (c.exp && c.exp * 1000 < Date.now()) return null; // expired token
    return c;
  } catch { return null; }
}

// ── local list cache (sidebar render source) ────────────────────────────────
export function listChats(userId) {
  try { return JSON.parse(localStorage.getItem(LIST_KEY(userId)) || '[]'); } catch { return []; }
}
function writeList(userId, chats) {
  localStorage.setItem(LIST_KEY(userId), JSON.stringify(chats.slice(0, MAX)));
  emit();
}
export function getChat(userId, convId) {
  return listChats(userId).find((c) => c.id === convId) || null;
}

// New chat: mint an id locally and show a stub immediately. The server session is
// created lazily on the first message (X-Hermes-Session-Id), then refreshChats()
// reconciles it (with the server-generated preview/title).
export function createChat(userId) {
  // Reuse a chat nobody has typed into yet (local stub, never titled) rather
  // than adding another: every visit to /agentbro and every New chat click
  // made one, and the list filled with empty "New chat" rows.
  const chats = listChats(userId);
  const empty = chats.find((c) => c.local && c.title === 'New chat' && !c.preview);
  if (empty) {
    const reused = { ...empty, updatedAt: Date.now() };
    writeList(userId, [reused, ...chats.filter((c) => c.id !== empty.id)]);
    return reused;
  }
  const id = `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const chat = { id, title: 'New chat', preview: '', updatedAt: Date.now(), local: true };
  writeList(userId, [chat, ...chats]);
  return chat;
}

/** Drop empty local stubs except the newest (cleans lists from before the
 * reuse above). */
export function pruneEmptyChats(userId) {
  const chats = listChats(userId);
  let kept = false;
  const next = chats.filter((c) => {
    if (!(c.local && c.title === 'New chat' && !c.preview)) return true;
    if (kept) return false;
    kept = true;
    return true;
  });
  if (next.length !== chats.length) writeList(userId, next);
}

export function setChatTitle(userId, convId, title) {
  // Local optimistic title for a brand-new chat; the server auto-titles and
  // refreshChats() overwrites with the authoritative preview. Best-effort only.
  const chats = listChats(userId);
  const i = chats.findIndex((c) => c.id === convId);
  if (i < 0) return;
  const cleaned = (title || '').replace(/\s+/g, ' ').trim().slice(0, 80);
  if (!cleaned) return;
  chats[i] = { ...chats[i], title: cleaned, updatedAt: Date.now() };
  writeList(userId, chats);
}

export function touchChat(userId, convId) {
  const chats = listChats(userId);
  const i = chats.findIndex((c) => c.id === convId);
  if (i < 0) return;
  const [hit] = chats.splice(i, 1);
  writeList(userId, [{ ...hit, updatedAt: Date.now() }, ...chats]);
}

// Delete: optimistic cache removal + real server delete when creds are available
// (so it's gone everywhere, not just this device). If the sandbox is asleep/no
// creds, the cache hides it and the server row is reconciled on next refresh.
export function deleteChat(userId, convId) {
  writeList(userId, listChats(userId).filter((c) => c.id !== convId));
  const creds = getCreds();
  if (creds) deleteSession(creds.baseUrl, creds.token, convId).catch(() => { /* best-effort */ });
}

// ── server sync ──────────────────────────────────────────────────────────────
async function api(baseUrl, token, path, opts = {}) {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error(`sessions ${res.status}`);
  return res.status === 204 ? null : res.json();
}

// Fetch the authoritative session list from Hermes and reconcile the cache.
// Merges server sessions with any still-local new-chat stubs (no server session
// yet — created on first message). Called by pages that hold a live session.
export async function refreshChats(userId, baseUrl, token) {
  const d = await api(baseUrl, token, '/sessions?limit=50&source=api_server');
  const server = (d?.data || []).map((s) => ({
    id: s.id,
    title: s.title || s.preview || 'New chat',
    preview: s.preview || '',
    updatedAt: Math.round((s.last_active || s.started_at || 0) * 1000),
    messageCount: s.message_count || 0,
  }));
  const serverIds = new Set(server.map((s) => s.id));
  const localStubs = listChats(userId).filter((c) => c.local && !serverIds.has(c.id));
  const merged = [...localStubs, ...server].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  writeList(userId, merged);
  return merged;
}

export async function deleteSession(baseUrl, token, id) {
  return api(baseUrl, token, `/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function renameSession(baseUrl, token, id, title) {
  return api(baseUrl, token, `/sessions/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title }),
  });
}

// Replay a conversation's transcript: GET /sessions/{id}/messages.
export async function getSessionMessages(baseUrl, token, id) {
  const d = await api(baseUrl, token, `/sessions/${encodeURIComponent(id)}/messages`);
  return d?.data || [];
}

const TOOL_EMOJI = {
  terminal: '💻', skill_view: '📚', skills_list: '📚', read_file: '📄', write_file: '✍️',
  view_image: '👁️', vision_analyze: '👁️', grounded_web_search: '🌐', web_search: '🌐',
};

// ── tool args/results from the session transcript (plan 179 Tier 1) ─────────
// The completions SSE deliberately strips tool arguments and results, but the
// session transcript (GET /sessions/{id}/messages — already used for
// history replay) carries both: assistant rows have tool_calls[].function
// .arguments and role=tool rows have the result keyed by tool_call_id.
// HermesChat polls this sparingly during a turn to enrich the live trail.

// Truncate results at INGEST, not render: routine --json results are 6–22KB
// each and a run makes dozens of calls — never buffer them whole in state.
const RESULT_CAP = 4096;

// Strip secret-looking values from a command line before display.
const SECRET_RE = /((?:api[_-]?key|key|token|secret|password|authorization)\s*[=:]\s*|Bearer\s+)(["']?)[A-Za-z0-9_\-./+]{8,}\2/gi;
export function redactSecrets(s) {
  return String(s || '').replace(SECRET_RE, '$1$2•••$2');
}

// arguments JSON → a one-line human command/summary for the step row.
export function summarizeToolArgs(name, argsJson) {
  let args = argsJson;
  if (typeof argsJson === 'string') {
    try { args = JSON.parse(argsJson); } catch { return redactSecrets(argsJson.slice(0, 300)); }
  }
  if (!args || typeof args !== 'object') return '';
  const primary = args.command || args.path || args.file_path || args.query || args.url
    || args.skill || args.name || Object.values(args).find((v) => typeof v === 'string');
  return redactSecrets(String(primary ?? '').slice(0, 300));
}

// role=tool content → { text (capped), preview, bytes }. Terminal results are
// JSON like {"output": "<stdout>"}; the stdout is often itself CLI --json output.
export function parseToolResult(content) {
  let text = String(content ?? '');
  const bytes = text.length;
  try {
    const outer = JSON.parse(text);
    if (outer && typeof outer === 'object' && typeof outer.output === 'string') text = outer.output;
  } catch { /* not JSON — keep raw */ }
  let preview = '';
  try {
    const data = JSON.parse(text);
    if (data && typeof data === 'object') {
      if (typeof data.count === 'number') preview = `${data.count.toLocaleString()} rows`;
      else if (Array.isArray(data.results)) preview = `${data.results.length} results`;
      else if (Array.isArray(data)) preview = `${data.length} items`;
      else preview = `${Object.keys(data).length} fields · ${(bytes / 1024).toFixed(1)} KB`;
    }
  } catch { /* not JSON */ }
  if (!preview) {
    const firstLine = text.split('\n').find((l) => l.trim());
    preview = (firstLine || '').trim().slice(0, 120);
  }
  return { text: text.slice(0, RESULT_CAP), truncated: text.length > RESULT_CAP, preview, bytes };
}

// Transcript rows → Map(call_id → {tool, command, result}) for trail enrichment.
export function extractToolMeta(rows) {
  const meta = new Map();
  for (const m of rows) {
    if (m.role === 'assistant' && Array.isArray(m.tool_calls)) {
      for (const c of m.tool_calls) {
        const id = c.id || c.call_id;
        if (!id) continue;
        const name = c.function?.name || c.tool || c.tool_name || 'tool';
        meta.set(id, { tool: name, command: summarizeToolArgs(name, c.function?.arguments), result: null });
      }
    } else if (m.role === 'tool' && m.tool_call_id) {
      const entry = meta.get(m.tool_call_id)
        || { tool: m.tool_name || 'tool', command: '', result: null };
      entry.result = parseToolResult(m.content);
      meta.set(m.tool_call_id, entry);
    }
  }
  return meta;
}

// Map stored Hermes messages → the UI's message shape. A "turn" is a user message
// followed by assistant/tool messages until the next user message; the assistant
// tool_calls become the activity trail and the final assistant text is the answer
// (mirrors the live streaming UI). role=tool rows attach as per-step results.
export function mapHermesMessages(rows) {
  const meta = extractToolMeta(rows);
  const out = [];
  let asst = null;
  for (const m of rows) {
    if (m.role === 'user') {
      asst = null;
      out.push({ role: 'user', content: m.content || '' });
    } else if (m.role === 'assistant') {
      if (!asst) { asst = { role: 'assistant', content: '', activity: [] }; out.push(asst); }
      // A row synced from the desktop (AnalystBro Front docs/12) carries its
      // OWN full `meta.tools`/`meta.images` directly -- no tool_calls array to
      // cross-reference, since there is no separate hermes-native tool-call
      // stream for a turn that never ran through hermes at all. Checked first
      // and returned early: a desktop-answered row has nothing else to add.
      if (m.meta && (Array.isArray(m.meta.tools) || Array.isArray(m.meta.images))) {
        for (const t of m.meta.tools || []) {
          asst.activity.push({ id: `d-${asst.activity.length}`, tool: '', emoji: '', label: t.label || '', status: 'completed', durationMs: t.ms ?? null });
        }
        for (const src of m.meta.images || []) {
          const match = /^data:([^;]+);base64,(.*)$/.exec(src || '');
          if (match) {
            if (!asst.images) asst.images = [];
            asst.images.push({ mimeType: match[1], data: match[2] });
          }
        }
        if (m.content) asst.content += m.content;
        continue;
      }
      if (Array.isArray(m.tool_calls)) {
        for (const c of m.tool_calls) {
          const id = c.id || c.call_id || `tool:${asst.activity.length}`;
          const name = c.function?.name || c.tool || c.tool_name || 'tool';
          const enriched = meta.get(id) || {};
          asst.activity.push({
            id,
            tool: name, emoji: TOOL_EMOJI[name] || '🔧',
            label: enriched.command || '', result: enriched.result || null,
            status: 'completed',
          });
        }
      }
      if (m.content) asst.content += m.content;
    }
  }
  return out;
}

// ── pub/sub so the Sidebar refreshes when the list changes ───────────────────
const listeners = new Set();
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function emit() { for (const fn of listeners) { try { fn(); } catch { /* ignore */ } } }

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key && (e.key.startsWith('agentbro_sessions_'))) emit();
  });
}
